import { z } from "zod";

/**
 * Machine codes → human copy → suggested action.
 * Unknown/unmapped codes fall through to UNKNOWN — never a blank screen.
 */

export const ErrorCodeSchema = z.enum([
  "API_UNREACHABLE",
  "SPOTIFY_TOKEN_EXPIRED",
  "SPOTIFY_NOT_CONNECTED",
  "SPOTIFY_RATE_LIMITED",
  "SPOTIFY_PREMIUM_REQUIRED",
  "PLAYBACK_UNAVAILABLE",
  "RESEARCH_JSON_INVALID",
  "SAVE_FAILED",
  "NOT_FOUND",
  "UNKNOWN",
]);

export type ErrorCode = z.infer<typeof ErrorCodeSchema>;

export const ErrorActionKindSchema = z.enum([
  "reconnect_spotify",
  "connect_spotify",
  "retry",
  "reload",
  "none",
]);

export type ErrorActionKind = z.infer<typeof ErrorActionKindSchema>;

export const ErrorSeveritySchema = z.enum(["info", "warning", "error"]);

export type ErrorSeverity = z.infer<typeof ErrorSeveritySchema>;

export interface ErrorAction {
  label: string;
  kind: ErrorActionKind;
}

export interface ErrorDefinition {
  code: ErrorCode;
  title: string;
  message: string;
  action: ErrorAction;
  severity: ErrorSeverity;
}

export const ERROR_REGISTRY: Record<ErrorCode, ErrorDefinition> = {
  API_UNREACHABLE: {
    code: "API_UNREACHABLE",
    title: "Can't reach CoyKoi",
    message:
      "Can't reach CoyKoi's server. It may be restarting — try again in a minute.",
    action: { label: "Retry", kind: "retry" },
    severity: "error",
  },
  SPOTIFY_TOKEN_EXPIRED: {
    code: "SPOTIFY_TOKEN_EXPIRED",
    title: "Spotify connection expired",
    message: "Your Spotify session ended. Reconnect to keep browsing your library.",
    action: { label: "Reconnect", kind: "reconnect_spotify" },
    severity: "warning",
  },
  SPOTIFY_NOT_CONNECTED: {
    code: "SPOTIFY_NOT_CONNECTED",
    title: "Spotify not connected",
    message: "Spotify isn't connected yet.",
    action: { label: "Connect", kind: "connect_spotify" },
    severity: "warning",
  },
  SPOTIFY_RATE_LIMITED: {
    code: "SPOTIFY_RATE_LIMITED",
    title: "Spotify is busy",
    message: "Spotify is limiting requests right now. Try again in a few minutes.",
    action: { label: "Retry", kind: "retry" },
    severity: "warning",
  },
  SPOTIFY_PREMIUM_REQUIRED: {
    code: "SPOTIFY_PREMIUM_REQUIRED",
    title: "Premium required",
    message: "Playback needs Spotify Premium. Everything else works.",
    action: { label: "OK", kind: "none" },
    severity: "info",
  },
  PLAYBACK_UNAVAILABLE: {
    code: "PLAYBACK_UNAVAILABLE",
    title: "Playback unavailable",
    message: "Playback couldn't start — this network may be blocking Spotify.",
    action: { label: "Retry", kind: "retry" },
    severity: "warning",
  },
  RESEARCH_JSON_INVALID: {
    code: "RESEARCH_JSON_INVALID",
    title: "Research JSON didn't match",
    message: "That JSON didn't match the expected shape — check the fields and try again.",
    action: { label: "OK", kind: "none" },
    severity: "warning",
  },
  SAVE_FAILED: {
    code: "SAVE_FAILED",
    title: "Couldn't save",
    message: "Couldn't save that change.",
    action: { label: "Retry", kind: "retry" },
    severity: "error",
  },
  NOT_FOUND: {
    code: "NOT_FOUND",
    title: "Not in your library",
    message: "That item isn't in your library any more.",
    action: { label: "OK", kind: "none" },
    severity: "info",
  },
  UNKNOWN: {
    code: "UNKNOWN",
    title: "Something went wrong",
    message: "Something unexpected happened. Copy the details below and send them over.",
    action: { label: "Reload", kind: "reload" },
    severity: "error",
  },
};

export function getErrorDefinition(code: ErrorCode): ErrorDefinition {
  return ERROR_REGISTRY[code];
}

/**
 * Map legacy / domain server codes onto the user-facing registry.
 * Unmapped values become UNKNOWN (never blank).
 */
export function normalizeErrorCode(
  raw: string | undefined,
  message = "",
): ErrorCode {
  if (raw) {
    const parsed = ErrorCodeSchema.safeParse(raw);
    if (parsed.success) return parsed.data;
  }

  const msg = message.toLowerCase();

  switch (raw) {
    case "unauthorized":
    case "not_connected":
      if (/expired|reconnect|session ended/i.test(message)) {
        return "SPOTIFY_TOKEN_EXPIRED";
      }
      return "SPOTIFY_NOT_CONNECTED";
    case "spotify_scope_required":
      return "SPOTIFY_TOKEN_EXPIRED";
    case "research_bad_json":
    case "research_validation":
    case "research_no_json":
      return "RESEARCH_JSON_INVALID";
    case "not_found":
    case "NOT_FOUND":
      return "NOT_FOUND";
    case "INTERNAL":
      return "UNKNOWN";
    default:
      break;
  }

  if (/premium/i.test(msg)) return "SPOTIFY_PREMIUM_REQUIRED";
  if (/rate.?limit|quota|429/i.test(msg)) return "SPOTIFY_RATE_LIMITED";
  if (/expired|session ended/i.test(msg) && /spotify/i.test(msg)) {
    return "SPOTIFY_TOKEN_EXPIRED";
  }
  if (/not connected/i.test(msg)) return "SPOTIFY_NOT_CONNECTED";

  return "UNKNOWN";
}
